// components/subscription/CreditManagement/index.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X } from "lucide-react";
import {
	purchaseCredits,
	selectCredits,
} from "../../../store/slices/creditSlice";
import { toast } from "sonner";
import { useAuth } from "../../../hooks/useAuth";
import {
	ModalOverlay,
	ModalContent,
	ModalHeader,
	CreditOptions,
	CreditOption,
	PriceTag,
	Summary,
	CloseButton,
} from "./styles";
import { Button } from "../../../styles/components";

const CreditManagement = ({ onClose }) => {
	const dispatch = useDispatch();
	const { user } = useAuth();
	const subscription = useSelector((state) => state.subscription.data);
	const credits = useSelector(selectCredits);
	const loading = useSelector((state) => state.credits.loading);

	const [selectedHours, setSelectedHours] = useState(0);

	const creditPackages = [
		{ hours: 2, label: "Small Pack" },
		{ hours: 4, label: "Medium Pack" },
		{ hours: 6, label: "Large Pack" },
	].filter((pack) => pack.hours <= subscription.features.addOnCredits.limit);

	const calculatePrice = (hours) => {
		return hours * (subscription?.features?.addOnCredits?.price || 0);
	};

	const handlePurchase = async () => {
		if (!selectedHours) {
			toast.error("Please select the number of hours to purchase", {
				style: { padding: "1rem" },
			});
			return;
		}

		try {
			await dispatch(
				purchaseCredits({
					userId: user.uid,
					hours: selectedHours,
				})
			).unwrap();

			toast.success("Credits purchased successfully!", {
				style: { padding: "1rem" },
			});
			onClose();
		} catch (error) {
			toast.error(error.message || "Failed to purchase credits", {
				style: { padding: "1rem" },
			});
		}
	};

	return (
		<ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
			<ModalContent>
				<ModalHeader>
					<h2>Purchase Additional Credits</h2>
					<CloseButton onClick={onClose}>
						<X size={24} />
					</CloseButton>
				</ModalHeader>

				<div className="content">
					<div className="current-credits">
						<h3>Current Credit Balance</h3>
						<p>{credits.available} hours available</p>
						<p className="limit-info">
							Monthly purchase limit:{" "}
							{subscription?.features?.addOnCredits?.limit || 0} hours
						</p>
					</div>

					<CreditOptions>
						{creditPackages.map((pack) => (
							<CreditOption
								key={pack.hours}
								selected={selectedHours === pack.hours}
								onClick={() => setSelectedHours(pack.hours)}
							>
								<h4>{pack.label}</h4>
								<div className="hours">{pack.hours} hours</div>
								<PriceTag>
									Rs{calculatePrice(pack.hours).toLocaleString()}
								</PriceTag>
							</CreditOption>
						))}
					</CreditOptions>

					{selectedHours > 0 && (
						<Summary>
							<div className="summary-item">
								<span>Selected Credits:</span>
								<span>{selectedHours} hours</span>
							</div>
							<div className="summary-item">
								<span>Price per Hour:</span>
								<span>Rs{subscription?.features?.addOnCredits?.price}</span>
							</div>
							<div className="summary-item total">
								<span>Total:</span>
								<span>Rs{calculatePrice(selectedHours).toLocaleString()}</span>
							</div>
						</Summary>
					)}

					<div className="button-group">
						<Button variant="outlined" onClick={onClose} disabled={loading}>
							Cancel
						</Button>
						<Button
							onClick={handlePurchase}
							disabled={!selectedHours || loading}
						>
							{loading ? "Processing..." : "Purchase Credits"}
						</Button>
					</div>
				</div>
			</ModalContent>
		</ModalOverlay>
	);
};

export default CreditManagement;
